import { createApi } from '@reduxjs/toolkit/query/react';

import { IUploadAvatarPayload } from '../../../types/interfaces/app-file';
import type { ICompleteRegistrationBody } from '../../../types/interfaces/complete-registration';
import { ISubscriptionRestriction } from '../../../types/interfaces/subscription-restriction';
import { IUser } from '../../../types/interfaces/user';
import {
  IUpsertMeasurementBody,
  IUserMeasurement,
} from '../../../types/interfaces/user-measurement';
import { getCachedMeForActiveFirebaseUser } from '../../../utils/auth/backend-session-cache';
import { API_CACHE_KEYS, ApiResponseCache } from '../../api-response-cache';
import { HTTP_METHOD, REDUX_CACHE, USERS } from '../../constants';
import * as endpoints from '../../endpoints';
import {
  TransformedErrorResponseWithNotification,
  getBaseQuery,
  getListProvidesTags,
  getUrlParamsString,
} from '../../generic';
import type { ICommunitiesApiCommunityInList } from '../communities/communities-api.types';
import type {
  ISubscriptionRestrictionPermission,
  IUsersApiGetMyDetailedResponse,
  IUsersApiGetMyOnboardingResponse,
  IUsersApiGetRejectedSubscriberRequestsResponse,
  IUsersApiGetSubscriberRequestsResponse,
  IUsersApiGetUserByIdResponse,
  IUsersApiGetUserSubscribersResponse,
  IUsersApiGetUserSubscriptionsResponse,
  IUsersApiGetUsersResponse,
  IUsersApiOnboardingCompletionAction,
} from './users-api.types';

export const usersApi = createApi({
  reducerPath: 'usersApi',
  tagTypes: [
    USERS.USERS,
    USERS.SELECTED_USER,
    USERS.ME,
    USERS.ME_ONBOARDING,
    USERS.ME_DETAILED,
    USERS.USER_COMMUNITIES,
    USERS.USER_SUBSCRIBERS,
    USERS.USER_SUBSCRIPTIONS,
    USERS.RESTRICTIONS,
    USERS.USER_SUBSCRIBER_REQUESTS,
    USERS.REJECTED_USER_SUBSCRIBER_REQUESTS,
    USERS.RESTRICTION_PERMISSIONS,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
    REDUX_CACHE.LAZY_LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getUsersApi: builder.query<
      IUsersApiGetUsersResponse,
      { searchValue: string; pageSize: number; page: number }
    >({
      keepUnusedDataFor: 3600,
      query({ searchValue, pageSize, page }) {
        const url =
          endpoints.User.ROOT +
          '?' +
          getUrlParamsString({
            searchValue: searchValue,
            pageSize: pageSize,
            page: page,
          });

        return {
          url,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, _, arg) => [
        { type: REDUX_CACHE.LAZY_LIST, id: arg.page + '_' + arg.searchValue },
      ],
    }),
    getMyDetailsApi: builder.query<IUser, void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.User.ME,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: USERS.ME }],
      transformResponse(response: IUser) {
        ApiResponseCache.set(API_CACHE_KEYS.ME, response);
        return response;
      },
      transformErrorResponse(response: any, meta: any) {
        if (response.status !== 'FETCH_ERROR') {
          ApiResponseCache.remove(API_CACHE_KEYS.ME);
        }

        return TransformedErrorResponseWithNotification().transformErrorResponse(
          response,
          meta,
        );
      },
    }),
    getMyDetailsDetailedApi: builder.query<
      IUsersApiGetMyDetailedResponse,
      void
    >({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.User.ME_DETAILED,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: USERS.ME_DETAILED }],
      ...TransformedErrorResponseWithNotification(),
    }),
    getMyOnboardingApi: builder.query<IUsersApiGetMyOnboardingResponse, void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.User.ME_ONBOARDING,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: () => [{ type: USERS.ME_ONBOARDING }],
      ...TransformedErrorResponseWithNotification(),
    }),
    getUserDetailsApi: builder.query<
      IUsersApiGetUserByIdResponse,
      { id: string }
    >({
      keepUnusedDataFor: 3600,
      query({ id }) {
        return {
          url: `${endpoints.User.ROOT}/${id}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: USERS.SELECTED_USER }],
      ...TransformedErrorResponseWithNotification(),
    }),
    getUserCommunitiesApi: builder.query<
      ICommunitiesApiCommunityInList[],
      string
    >({
      keepUnusedDataFor: 3600,
      query(userId) {
        return {
          url: endpoints.User.COMMUNITIES.replace('${1}', userId),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, USERS.USER_COMMUNITIES),
      ...TransformedErrorResponseWithNotification(),
    }),
    getUserSubscribersApi: builder.query<
      IUsersApiGetUserSubscribersResponse,
      string
    >({
      keepUnusedDataFor: 3600,
      query(userId) {
        return {
          url: endpoints.User.SUBSCRIBERS.replace('${1}', userId),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, USERS.USER_SUBSCRIBERS),
    }),
    getUserSubscriptionsApi: builder.query<
      IUsersApiGetUserSubscriptionsResponse,
      string
    >({
      keepUnusedDataFor: 3600,
      query(userId) {
        return {
          url: endpoints.User.SUBSCRIPTIONS.replace('${1}', userId),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, USERS.USER_SUBSCRIPTIONS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getUserSubscriberRequestsApi: builder.query<
      IUsersApiGetSubscriberRequestsResponse,
      void
    >({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.User.SUBSCRIBER_REQUESTS,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, USERS.USER_SUBSCRIBER_REQUESTS),
    }),

    getRejectedUserSubscriberRequestsApi: builder.query<
      IUsersApiGetRejectedSubscriberRequestsResponse,
      void
    >({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.User.REJECTED_SUBSCRIBER_REQUESTS,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, USERS.REJECTED_USER_SUBSCRIBER_REQUESTS),
    }),
    updateMyConfigApi: builder.mutation<
      { success: boolean },
      { config: Array<{ key: string; value: string | null }> }
    >({
      query({ config }) {
        return {
          url: endpoints.User.ME_CONFIG,
          method: HTTP_METHOD.PUT,
          body: { config },
        };
      },
      invalidatesTags: (result, error) =>
        error
          ? [REDUX_CACHE.ABORT_INVALIDATION]
          : [{ type: USERS.ME }, { type: USERS.ME_DETAILED }],
      ...TransformedErrorResponseWithNotification(),
    }),
    putRegistrationCompletionApi: builder.mutation<
      { success: boolean },
      ICompleteRegistrationBody
    >({
      query(body) {
        return {
          url: endpoints.User.ME_REGISTRATION_COMPLETION,
          method: HTTP_METHOD.PUT,
          body,
        };
      },
      invalidatesTags: (result, error) =>
        error
          ? [REDUX_CACHE.ABORT_INVALIDATION]
          : [{ type: USERS.ME }, { type: USERS.ME_DETAILED }],
      ...TransformedErrorResponseWithNotification(),
    }),
    postUpsertMeasurementApi: builder.mutation<
      IUserMeasurement,
      IUpsertMeasurementBody
    >({
      query(body) {
        return {
          url: endpoints.User.ME_MEASUREMENTS,
          method: HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error) =>
        error
          ? [REDUX_CACHE.ABORT_INVALIDATION]
          : [{ type: USERS.ME_DETAILED }],
      ...TransformedErrorResponseWithNotification(),
    }),
    postOnboardingBlockCompletionApi: builder.mutation<
      { success: boolean },
      {
        blockKey: string;
        action: IUsersApiOnboardingCompletionAction;
      }
    >({
      query({ blockKey, action }) {
        return {
          url: endpoints.User.ME_ONBOARDING_BLOCK_COMPLETIONS.replace(
            '${1}',
            blockKey,
          ),
          method: HTTP_METHOD.POST,
          body: { action },
        };
      },
      invalidatesTags: (result, error) =>
        error
          ? [REDUX_CACHE.ABORT_INVALIDATION]
          : [{ type: USERS.ME }, { type: USERS.ME_ONBOARDING }],
      ...TransformedErrorResponseWithNotification(),
    }),
    postOnboardingSkipAllApi: builder.mutation<{ success: boolean }, void>({
      query() {
        return {
          url: endpoints.User.ME_ONBOARDING_SKIP_ALL,
          method: HTTP_METHOD.POST,
        };
      },
      invalidatesTags: (result, error) =>
        error
          ? [REDUX_CACHE.ABORT_INVALIDATION]
          : [{ type: USERS.ME }, { type: USERS.ME_ONBOARDING }],
      ...TransformedErrorResponseWithNotification(),
    }),
    postOnboardingResetApi: builder.mutation<{ success: boolean }, void>({
      query() {
        return {
          url: endpoints.User.ME_ONBOARDING_RESET,
          method: HTTP_METHOD.POST,
        };
      },
      invalidatesTags: (result, error) =>
        error
          ? [REDUX_CACHE.ABORT_INVALIDATION]
          : [{ type: USERS.ME }, { type: USERS.ME_ONBOARDING }],
      ...TransformedErrorResponseWithNotification(),
    }),
    updateUserApi: builder.mutation<
      void,
      {
        id: string;
        body: Partial<Omit<IUser, 'avatar'>> & {
          avatar?: IUploadAvatarPayload;
        };
      }
    >({
      query({ id, body }) {
        return {
          url: `${endpoints.User.ROOT}/${id}`,
          method: HTTP_METHOD.PUT,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: USERS.USERS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: USERS.SELECTED_USER,
            },
            {
              type: USERS.ME,
            },
            {
              type: USERS.ME_DETAILED,
            },
            {
              type: USERS.USERS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    subscribeToUserApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: endpoints.User.SUBSCRIBE.replace('${1}', id),
          method: HTTP_METHOD.POST,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            { type: REDUX_CACHE.LAZY_LIST },
            {
              type: USERS.USERS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: USERS.SELECTED_USER,
            },
            {
              type: USERS.USER_SUBSCRIPTIONS,
            },
            {
              type: USERS.USER_SUBSCRIBERS,
            },
            {
              type: USERS.ME,
            },
            {
              type: USERS.ME_DETAILED,
            },
            {
              type: USERS.USER_SUBSCRIBER_REQUESTS,
            },
            {
              type: USERS.REJECTED_USER_SUBSCRIBER_REQUESTS,
            },
            {
              type: USERS.USERS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    unsubscribeFromUserApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: endpoints.User.UNSUBSCRIBE.replace('${1}', id),
          method: HTTP_METHOD.POST,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            { type: REDUX_CACHE.LAZY_LIST },
            {
              type: USERS.USERS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: USERS.ME,
            },
            {
              type: USERS.ME_DETAILED,
            },
            {
              type: USERS.USER_SUBSCRIPTIONS,
            },
            {
              type: USERS.USER_SUBSCRIBERS,
            },
            {
              type: USERS.USER_SUBSCRIBER_REQUESTS,
            },
            {
              type: USERS.REJECTED_USER_SUBSCRIBER_REQUESTS,
            },
            {
              type: USERS.SELECTED_USER,
            },
            {
              type: USERS.USERS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    changeUserSubscriptionStatusApi: builder.mutation<
      void,
      { id: string; body: { status: string } }
    >({
      // todo add statuses
      query({ id, body }) {
        return {
          url: endpoints.User.STATUS.replace('${1}', id),
          method: HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            { type: REDUX_CACHE.LAZY_LIST },
            {
              type: USERS.USERS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: USERS.USER_SUBSCRIBERS,
            },
            {
              type: USERS.USER_SUBSCRIPTIONS,
            },
            {
              type: USERS.USER_SUBSCRIBER_REQUESTS,
            },
            {
              type: USERS.REJECTED_USER_SUBSCRIBER_REQUESTS,
            },
            {
              type: USERS.SELECTED_USER,
            },
            {
              type: USERS.ME,
            },
            {
              type: USERS.ME_DETAILED,
            },
            {
              type: USERS.USERS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    deleteUserApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.User.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: USERS.USERS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getRestrictionsPermissionsApi: builder.query<
      ISubscriptionRestrictionPermission,
      void
    >({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.User.RESTRICTION_PERMISSIONS,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: USERS.RESTRICTION_PERMISSIONS }],
      ...TransformedErrorResponseWithNotification(),
    }),
  }),
});

export const resetUserApiState = () => usersApi.util.resetApiState();

export const {
  useGetUsersApiQuery: useGetUsers,
  useLazyGetUsersApiQuery: useLazyGetUsers,
  useGetUserDetailsApiQuery: useGetUserDetails,
  useGetMyDetailsDetailedApiQuery: useGetMyDetailsDetailed,
  useLazyGetMyDetailsDetailedApiQuery: useLazyGetMyDetailsDetailed,
  useGetMyOnboardingApiQuery: useGetMyOnboarding,
  useLazyGetMyOnboardingApiQuery: useLazyGetMyOnboarding,
  useGetUserCommunitiesApiQuery: useGetUserCommunities,
  useGetUserSubscribersApiQuery: useGetUserSubscribers,
  useLazyGetUserSubscribersApiQuery: useLazyGetUserSubscribers,
  useGetUserSubscriptionsApiQuery: useGetUserSubscriptions,
  useLazyGetUserSubscriptionsApiQuery: useLazyGetUserSubscriptions,
  useLazyGetUserDetailsApiQuery: useLazyGetUserDetails,
  useUpdateMyConfigApiMutation: useUpdateMyConfig,
  usePutRegistrationCompletionApiMutation: usePutRegistrationCompletion,
  usePostUpsertMeasurementApiMutation: usePostUpsertMeasurement,
  usePostOnboardingBlockCompletionApiMutation: usePostOnboardingBlockCompletion,
  usePostOnboardingSkipAllApiMutation: usePostOnboardingSkipAll,
  usePostOnboardingResetApiMutation: usePostOnboardingReset,
  useUpdateUserApiMutation: useUpdateUser,
  useSubscribeToUserApiMutation: useSubscribeToUser,
  useUnsubscribeFromUserApiMutation: useUnsubscribeFromUser,
  useChangeUserSubscriptionStatusApiMutation: useChangeUserSubscriptionStatus,
  useDeleteUserApiMutation: useDeleteUser,
  useGetUserSubscriberRequestsApiQuery: useGetUserSubscriberRequests,
  useGetRejectedUserSubscriberRequestsApiQuery:
    useGetRejectedUserSubscriberRequests,
  useLazyGetUserSubscriberRequestsApiQuery: useLazyGetUserSubscriberRequests,
  useLazyGetRejectedUserSubscriberRequestsApiQuery:
    useLazyGetRejectedUserSubscriberRequests,
  useGetRestrictionsPermissionsApiQuery: useGetRestrictionsPermissions,
} = usersApi;

const { useGetMyDetailsApiQuery, useLazyGetMyDetailsApiQuery } = usersApi;

export { useGetMyDetailsApiQuery };

/** Returns me from API when loaded; until then returns cached value from localStorage (no Redux prefill). Response replaces cache on success. */
export const useGetMyDetails = (options?: { skip?: boolean }) => {
  const result = useGetMyDetailsApiQuery(undefined, {
    skip: options?.skip ?? false,
  });
  const cached = getCachedMeForActiveFirebaseUser();
  return {
    ...result,
    data: result.data ?? cached ?? undefined,
  };
};

/** Same as useGetMyDetails but lazy: trigger returns cached as data until response arrives. */
export const useLazyGetMyDetails = () => {
  const [trigger, result] = useLazyGetMyDetailsApiQuery();
  const cached = getCachedMeForActiveFirebaseUser();
  return [
    trigger,
    {
      ...result,
      data: result.data ?? cached ?? undefined,
    },
  ] as const;
};
